/**
 * ラリー地図の絞り込みバー
 *
 * 「地図に何を出すか」は獲得状況・ラリー・他のプラン・都道府県の 4 つあるが、よく触るのは獲得状況だけ。
 * 獲得状況だけ 1 タップで押せる位置に置き、残りは漏斗ボタンの 2 階層メニューへ寄せる。
 * 都道府県が 47 件あっても高さが変わらないよう、一覧は 1 つずつ差し替えて出す。
 */

import { ArrowUpDown, Check, ChevronLeft, ChevronRight, ListFilter, MapPinned, Search } from 'lucide-react'
import { useState } from 'react'

import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import type { StampFilter } from './stamp-filter'
import { StampFilterTabs } from './stamp-filter-tabs'
import { type PlanLegendEntry, RALLY_FALLBACK_COLOR, type RallyLegendEntry } from './use-rally-places'

export type SortOption = { value: string; label: string }

/** この件数を超えたら一覧の上に検索欄を出す */
const SEARCHABLE_FROM = 8

type Props = {
  count: number
  selectedCount: number
  /** 渡すと件数の隣に「すべて選択」を出す。絞り込んだ結果をまとめて選ぶ用 */
  onSelectAll?: () => void
  stampFilter: StampFilter
  onStampFilterChange: (value: StampFilter) => void
  legend: RallyLegendEntry[]
  hiddenRallyIds: number[]
  onToggleRally: (rallyId: number) => void
  onToggleAllRallies?: (hidden: boolean) => void
  /** 他のプランに追加済みのスポットを持つプラン。空なら項目ごと出さない */
  planLegend?: PlanLegendEntry[]
  hiddenPlanIds?: number[]
  onTogglePlan?: (planId: number) => void
  onToggleAllPlans?: (hidden: boolean) => void
  /** 住所から拾った都道府県。2 つ以上あるときだけ選べる */
  prefectures?: string[]
  selectedPrefectures?: string[]
  onTogglePrefecture?: (value: string) => void
  onClearPrefectures?: () => void
  /** 渡すと漏斗の隣に並び替えアイコンを出す */
  sortOptions?: SortOption[]
  sortValue?: string
  onSortChange?: (value: string) => void
  className?: string
}

type View = 'root' | 'rallies' | 'plans' | 'prefectures'

const SummaryRow = ({ label, value, onClick }: { label: string; value: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors hover:bg-accent"
  >
    <span className="min-w-0 flex-1 truncate">{label}</span>
    <span className="shrink-0 tabular-nums text-muted-foreground">{value}</span>
    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
  </button>
)

const ListRow = ({
  label,
  count,
  checked,
  onClick,
  color,
}: {
  label: string
  count?: number
  checked: boolean
  onClick: () => void
  /** ラリーは地図の色と対応づける */
  color?: string
}) => (
  <button
    onClick={onClick}
    aria-pressed={checked}
    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent"
  >
    <Check className={cn('size-3.5 shrink-0', !checked && 'opacity-0')} />
    {color && <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />}
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {count !== undefined && <span className="shrink-0 tabular-nums text-muted-foreground">{count}</span>}
  </button>
)

type ListViewProps = {
  title: string
  onBack: () => void
  actionLabel: string
  onAction: () => void
  searchable: boolean
  keyword: string
  onKeywordChange: (value: string) => void
  children: React.ReactNode
}

const ListView = ({
  title,
  onBack,
  actionLabel,
  onAction,
  searchable,
  keyword,
  onKeywordChange,
  children,
}: ListViewProps) => (
  <>
    <div className="flex items-center gap-1 px-1 pb-1">
      <button onClick={onBack} aria-label="戻る" className="flex size-6 items-center justify-center rounded-md">
        <ChevronLeft className="size-4" />
      </button>
      <span className="min-w-0 flex-1 truncate text-xs">{title}</span>
      <button onClick={onAction} className="shrink-0 text-[10px] text-muted-foreground underline">
        {actionLabel}
      </button>
    </div>

    {searchable && (
      <div className="relative px-1 pb-1">
        <Search size={14} className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={keyword}
          onChange={(e) => onKeywordChange(e.target.value)}
          placeholder="絞り込む"
          className="h-8 pl-8 text-xs"
        />
      </div>
    )}

    <div className="max-h-64 overflow-y-auto">{children}</div>
  </>
)

export const RallyFilterBar = ({
  count,
  selectedCount,
  onSelectAll,
  stampFilter,
  onStampFilterChange,
  legend,
  hiddenRallyIds,
  onToggleRally,
  onToggleAllRallies,
  planLegend = [],
  hiddenPlanIds = [],
  onTogglePlan,
  onToggleAllPlans,
  prefectures = [],
  selectedPrefectures = [],
  onTogglePrefecture,
  onClearPrefectures,
  sortOptions = [],
  sortValue,
  onSortChange,
  className,
}: Props) => {
  const [view, setView] = useState<View>('root')
  const [keyword, setKeyword] = useState('')

  const showRallies = legend.length > 1
  const showPlans = planLegend.length > 0 && onTogglePlan !== undefined
  const showPrefectures = prefectures.length > 1 && onTogglePrefecture !== undefined
  const showSort = sortOptions.length > 1 && onSortChange !== undefined

  const shownRallies = legend.filter(({ rally }) => !hiddenRallyIds.includes(rally.id)).length
  const shownPlans = planLegend.filter(({ plan }) => !hiddenPlanIds.includes(plan.id)).length
  const allRalliesHidden = shownRallies === 0
  const allPlansHidden = shownPlans === 0
  // 隠している数と選んでいる県の数が「効いている絞り込み」の数。0 ならバッジは出さない
  const activeCount =
    (showRallies ? legend.length - shownRallies : 0) +
    (showPlans ? planLegend.length - shownPlans : 0) +
    (showPrefectures ? selectedPrefectures.length : 0)

  const needle = keyword.trim().toLowerCase()
  const hit = (text: string) => !needle || text.toLowerCase().includes(needle)

  const openView = (next: View) => {
    setKeyword('')
    setView(next)
  }

  const backToRoot = () => {
    setKeyword('')
    setView('root')
  }

  const prefectureSummary =
    selectedPrefectures.length === 0
      ? 'すべて'
      : selectedPrefectures.length === 1
        ? selectedPrefectures[0]
        : `${selectedPrefectures.length} 件`

  return (
    <div className={cn('flex items-center justify-between gap-3 bg-background px-3 py-2', className)}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex shrink-0 items-center gap-1 text-xs tabular-nums text-muted-foreground">
          <MapPinned className="size-4" />
          {count}
          {selectedCount > 0 && ` / ${selectedCount} 選択中`}
        </span>
        {onSelectAll && count > 0 && (
          <button onClick={onSelectAll} className="shrink-0 text-xs text-primary underline underline-offset-2">
            すべて選択
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {(showRallies || showPlans || showPrefectures) && (
          <Popover onOpenChange={(open) => !open && backToRoot()}>
            <PopoverTrigger className="relative flex size-8 items-center justify-center rounded-md border">
              <ListFilter className="size-4" />
              {activeCount > 0 && (
                <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[9px] tabular-nums text-primary-foreground">
                  {activeCount}
                </span>
              )}
            </PopoverTrigger>
            {/* 常設シートより手前に出さないと地図の上で隠れる */}
            <PopoverContent align="end" className="z-[10000000] w-64 p-2">
              {view === 'root' && (
                <>
                  {showRallies && (
                    <SummaryRow
                      label="ラリー"
                      value={`${shownRallies} / ${legend.length}`}
                      onClick={() => openView('rallies')}
                    />
                  )}
                  {showPlans && (
                    <SummaryRow
                      label="他のプランの分"
                      value={`${shownPlans} / ${planLegend.length}`}
                      onClick={() => openView('plans')}
                    />
                  )}
                  {showPrefectures && (
                    <SummaryRow label="都道府県" value={prefectureSummary} onClick={() => openView('prefectures')} />
                  )}
                </>
              )}

              {view === 'rallies' && (
                <ListView
                  title="ラリー"
                  onBack={backToRoot}
                  actionLabel={allRalliesHidden ? 'すべて表示' : 'すべて隠す'}
                  onAction={() => onToggleAllRallies?.(!allRalliesHidden)}
                  searchable={legend.length >= SEARCHABLE_FROM}
                  keyword={keyword}
                  onKeywordChange={setKeyword}
                >
                  {legend
                    .filter(({ rally }) => hit(rally.title))
                    .map(({ rally, count: rallyCount }) => (
                      <ListRow
                        key={rally.id}
                        label={rally.title}
                        count={rallyCount}
                        checked={!hiddenRallyIds.includes(rally.id)}
                        onClick={() => onToggleRally(rally.id)}
                        color={rally.color ?? RALLY_FALLBACK_COLOR}
                      />
                    ))}
                </ListView>
              )}

              {view === 'plans' && onTogglePlan && (
                <ListView
                  title="他のプランの分"
                  onBack={backToRoot}
                  actionLabel={allPlansHidden ? 'すべて表示' : 'すべて隠す'}
                  onAction={() => onToggleAllPlans?.(!allPlansHidden)}
                  searchable={planLegend.length >= SEARCHABLE_FROM}
                  keyword={keyword}
                  onKeywordChange={setKeyword}
                >
                  {planLegend
                    .filter(({ plan }) => hit(plan.title))
                    .map(({ plan, count: planCount }) => (
                      <ListRow
                        key={plan.id}
                        label={plan.title}
                        count={planCount}
                        checked={!hiddenPlanIds.includes(plan.id)}
                        onClick={() => onTogglePlan(plan.id)}
                      />
                    ))}
                </ListView>
              )}

              {view === 'prefectures' && onTogglePrefecture && (
                <ListView
                  title="都道府県"
                  onBack={backToRoot}
                  actionLabel="すべて解除"
                  onAction={() => onClearPrefectures?.()}
                  searchable={prefectures.length >= SEARCHABLE_FROM}
                  keyword={keyword}
                  onKeywordChange={setKeyword}
                >
                  {prefectures
                    .filter((name) => hit(name))
                    .map((name) => (
                      <ListRow
                        key={name}
                        label={name}
                        checked={selectedPrefectures.includes(name)}
                        onClick={() => onTogglePrefecture(name)}
                      />
                    ))}
                </ListView>
              )}
            </PopoverContent>
          </Popover>
        )}

        {showSort && (
          <Popover>
            <PopoverTrigger className="flex size-8 items-center justify-center rounded-md border" aria-label="並び替え">
              <ArrowUpDown className="size-4" />
            </PopoverTrigger>
            <PopoverContent align="end" className="z-[10000000] w-56 p-2">
              {sortOptions.map((option) => (
                <ListRow
                  key={option.value}
                  label={option.label}
                  checked={sortValue === option.value}
                  onClick={() => onSortChange(option.value)}
                />
              ))}
            </PopoverContent>
          </Popover>
        )}

        <StampFilterTabs value={stampFilter} onChange={onStampFilterChange} className="shrink-0" />
      </div>
    </div>
  )
}
